import './HeroSection.scss';

import React, { useState } from 'react';
import classNames from 'classnames';

import {
  Banner,
  BannerDescription,
  BannerEyebrow,
  BannerFootnote,
  BannerImage,
  BannerImageContainer,
  BannerLinks,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Button,
  FluidImage,
  Icon,
  List,
  ListItem,
  P,
  Typography,
} from '@vp/swan';

import {
  WIX_PRIVACY_POLICY_PAGER_URL,
  WIX_TERMS_OF_USE_PAGER_URL,
} from '@vp/digital-url-service-lib';
import {
  FeatureModel,
  HeroModel,
  MediaModel,
} from '@vp/digital-common-data-model-lib';
import { isProduction } from '@vp/digital-environment-lib';
import { LinkFactory, TokenReplacer } from '@vp/digital-token-replacer-lib';

import {
  GET_STARTED_CTA_DETAIL,
  MAIN_HERO_DATA_SECTION_LABEL,
  trackMainHeroLinkClick,
} from '../WixWebsitesAnalytics';

import MissingContentAlert from '../../common/MissingContentAlert';

interface HeroSectionProps {
  content: HeroModel;
  pageContext: any;
  isWebsShutdown?: boolean;
  handleGetStartedNavigation?: () => void;
}

const HeroSection = ({
  content,
  pageContext,
  isWebsShutdown,
  handleGetStartedNavigation,
}: HeroSectionProps) => {
  if (!content) {
    return <MissingContentAlert />;
  }

  const { urlWixPartnerGetStartedTnCAcceptedUrls } = pageContext;

  const urlWixPartnerGetStartedTncAcceptedUrl = isProduction()
    ? urlWixPartnerGetStartedTnCAcceptedUrls.prod
    : urlWixPartnerGetStartedTnCAcceptedUrls.qa;

  return (
    <Banner
      mb={{ sm: 5, md: 5, lg: 7, xl: 7 }}
      bgc="standard"
      height="short"
      pr={{ sm: 0, md: 0, lg: 0, xl: 0 }}
    >
      <HeroImage
        className="wix-websites-pdp__hero__image-mobile"
        mainImage={content.mediaList[0]}
      />

      <BannerTextContainer>
        <BannerText>
          <BannerEyebrow>
            <FluidImage
              className="wix-websites-pdp__hero__logo"
              srcSet={content.mediaList[1].media.resourceSetUrl}
              src={content.mediaList[1].media.secureUrl}
              alt={content.mediaList[1].media.fileName}
              sizes="170px"
            />
          </BannerEyebrow>

          <BannerTitle component="h1" fontSize="x4large">
            {content.title}
          </BannerTitle>

          <BannerDescription component="p">
            {content.descriptionLong}
          </BannerDescription>

          <BannerDescription mt={6} textAlign="left">
            <P fontWeight="bold" fontSize="large">
              {content.heroFeatureGroup.title}
            </P>

            <List skin="minimal" mt={4}>
              {content.heroFeatureGroup.features.map((item: FeatureModel) => (
                <ListItem key={item.title}>
                  <Icon iconType="check" size="24p" skin="standard" mr={4} />

                  {item.title}
                </ListItem>
              ))}
            </List>
          </BannerDescription>

          <BannerLinks>
            <Button
              skin="secondary"
              iconPosition="right"
              data-position="1"
              data-translation={GET_STARTED_CTA_DETAIL}
              data-section={MAIN_HERO_DATA_SECTION_LABEL}
              className="wix-websites-pdp__hero__cta"
              onClick={() => {
                if (!isWebsShutdown) {
                  trackMainHeroLinkClick(urlWixPartnerGetStartedTncAcceptedUrl);
                }
                handleGetStartedNavigation();
              }}
            >
              {content.actions[0].title}
              <Icon iconType="arrowRight" size="20p" />
            </Button>
          </BannerLinks>

          <BannerFootnote
            textColor="info"
            className="wix-websites-pdp__hero__footer"
          >
            <Typography as="span">
              <TokenReplacer
                template={content.heroMetadata[0].description}
                replacements={{
                  link: LinkFactory({
                    wixTermsOfUsePagerUrl: WIX_TERMS_OF_USE_PAGER_URL,
                    wixPrivacyPolicyPagerUrl: WIX_PRIVACY_POLICY_PAGER_URL,
                  }),
                }}
                convertMarkdown
              />
            </Typography>
          </BannerFootnote>
        </BannerText>
      </BannerTextContainer>

      <HeroImage
        className="wix-websites-pdp__hero__image-desktop"
        mainImage={content.mediaList[0]}
      />
    </Banner>
  );
};

interface HeroImageProps {
  className: string;
  mainImage: MediaModel;
}

const HeroImage = ({ className, mainImage }: HeroImageProps) => {
  return (
    <BannerImageContainer className={className}>
      <BannerImage className={classNames('wix-websites-pdp__hero__image')}>
        <FluidImage
          alt={mainImage.altText}
          srcSet={mainImage.media.resourceSetUrl}
          src={mainImage.media.secureUrl}
          sizes="(max-width: 768px) 100vw, 
            (max-width: 2840px) 50vw, 
            1420px"
        />
      </BannerImage>
    </BannerImageContainer>
  );
};

export default HeroSection;
